import { useState, useEffect, useRef } from "react";
import { Save, Plus, Trash2, Upload, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Section({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#FF5500]/20 bg-[#0c1128] mb-4">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 hover:bg-[#0c1128]/80 transition-colors">
        <h3 className="font-['Black_Han_Sans'] text-lg text-[#F4EFE6] uppercase">{title}</h3>
        {open ? <ChevronUp size={16} className="text-[#FF5500]" /> : <ChevronDown size={16} className="text-[#FF5500]" />}
      </button>
      {open && <div className="p-4 pt-0 border-t border-[#FF5500]/10">{children}</div>}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", rows }) {
  return (
    <div className="mb-4">
      <label className="font-mono-label text-xs text-[#FF5500] mb-1 block">{label}</label>
      {type === "textarea" ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows || 3}
          className="w-full bg-[#080810] border border-[#FF5500]/20 text-[#F4EFE6] text-sm p-3 focus:outline-none focus:border-[#FF5500] resize-y" />
      ) : (
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#080810] border border-[#FF5500]/20 text-[#F4EFE6] text-sm p-2 focus:outline-none focus:border-[#FF5500]" />
      )}
    </div>
  );
}

function ImageField({ label, value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("group", "cms");
      const { data } = await axios.post(`${API}/upload`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      onChange(`/api/files/${data.path}`);
      toast.success("Uploaded!");
    } catch { toast.error("Upload failed."); }
    finally { setUploading(false); }
  };

  return (
    <div className="mb-4">
      <label className="font-mono-label text-xs text-[#FF5500] mb-1 block">{label}</label>
      <div className="flex items-center gap-3">
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-[#080810] border border-[#FF5500]/20 text-[#F4EFE6] text-sm p-2 focus:outline-none focus:border-[#FF5500]"
          placeholder="Image URL or upload" />
        <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="shrink-0 bg-[#FF5500] text-[#F4EFE6] px-3 py-2 text-xs hover:bg-[#FF5500]/80 disabled:opacity-50">
          <Upload size={14} />
        </button>
      </div>
      {value && <img src={value.startsWith("/") ? `${BACKEND}${value}` : value} alt="" className="mt-2 h-20 object-cover border border-[#FF5500]/10" />}
    </div>
  );
}

function ListEditor({ label, items, setItems, fields }) {
  const add = () => {
    const newItem = {};
    fields.forEach(f => { newItem[f.key] = ""; });
    setItems([newItem, ...items]);
  };
  const update = (idx, key, val) => {
    const copy = [...items];
    copy[idx] = { ...copy[idx], [key]: val };
    setItems(copy);
  };
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <label className="font-mono-label text-xs text-[#FF5500]">{label}</label>
        <button onClick={add} className="flex items-center gap-1 text-[#FF5500] text-xs hover:text-[#F4EFE6]">
          <Plus size={12} /> Add
        </button>
      </div>
      {items.map((item, i) => (
        <div key={`${label}-${item.title || item.name || ''}-${i}`} className="flex items-start gap-2 mb-2 p-3 bg-[#080810] border border-[#FF5500]/10">
          <div className="flex-1 space-y-2">
            {fields.map(f => (
              <input key={f.key} value={item[f.key] || ""} onChange={(e) => update(i, f.key, e.target.value)}
                placeholder={f.label} className="w-full bg-transparent border-b border-[#FF5500]/15 text-[#F4EFE6] text-sm py-1 focus:outline-none focus:border-[#FF5500] placeholder:text-[#F4EFE6]/20" />
            ))}
          </div>
          <button onClick={() => remove(i)} className="text-[#F4EFE6]/30 hover:text-red-500 mt-1"><Trash2 size={14} /></button>
        </div>
      ))}
    </div>
  );
}

export default function ContentEditor() {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content`, { withCredentials: true })
      .then(({ data }) => setContent(data))
      .catch(() => toast.error("Failed to load content."))
      .finally(() => setLoading(false));
  }, []);

  const update = (section, key, value) => {
    setContent(prev => ({
      ...prev,
      [section]: { ...prev[section], [key]: value }
    }));
  };

  const updateNested = (section, parent, key, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [parent]: { ...(prev[section]?.[parent] || {}), [key]: value }
      }
    }));
  };

  const saveSection = async (section) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content/${section}`, { data: content[section] || {} }, { withCredentials: true });
      toast.success(`${section} saved!`);
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      for (const section of Object.keys(content)) {
        await axios.put(`${API}/admin/content/${section}`, { data: content[section] || {} }, { withCredentials: true });
      }
      toast.success("All content saved!");
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  if (loading) return <p className="text-[#F4EFE6]/60">Loading content...</p>;

  const home = content.home || {};
  const music = content.music || {};
  const shows = content.shows || {};
  const about = content.about || {};
  const contact = content.contact || {};
  const social = content.social || {};

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-['Black_Han_Sans'] text-3xl text-[#F4EFE6] uppercase">Site Content</h2>
        <button onClick={saveAll} disabled={saving}
          className="flex items-center gap-2 bg-[#FF5500] text-[#F4EFE6] font-['Black_Han_Sans'] uppercase tracking-wider px-6 py-2 hover:bg-[#FF5500]/80 disabled:opacity-50"
          data-testid="save-all-content">
          <Save size={16} /> {saving ? "SAVING..." : "SAVE ALL"}
        </button>
      </div>

      {/* HOME */}
      <Section title="Home Page" defaultOpen>
        <Field label="Tagline" value={home.tagline} onChange={(v) => update("home", "tagline", v)} />
        <Field label="Bio Text" value={home.bio} onChange={(v) => update("home", "bio", v)} type="textarea" rows={3} />
        <h4 className="font-mono-label text-xs text-[#FF5500] mt-4 mb-2">Featured Release</h4>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Title" value={home.featured_release?.title} onChange={(v) => updateNested("home", "featured_release", "title", v)} />
          <Field label="Label" value={home.featured_release?.label} onChange={(v) => updateNested("home", "featured_release", "label", v)} />
          <Field label="Year" value={home.featured_release?.year} onChange={(v) => updateNested("home", "featured_release", "year", v)} />
          <Field label="Spotify Link" value={home.featured_release?.spotify_url} onChange={(v) => updateNested("home", "featured_release", "spotify_url", v)} />
        </div>
        <ImageField label="Release Artwork" value={home.featured_release?.artwork_url} onChange={(v) => updateNested("home", "featured_release", "artwork_url", v)} />
        <h4 className="font-mono-label text-xs text-[#FF5500] mt-4 mb-2">Photo Grid (3 images)</h4>
        {(home.photos || []).map((url, i) => (
          <ImageField key={`photo-${i}`} label={`Photo ${i + 1}`} value={url} onChange={(v) => {
            const photos = [...(home.photos || [])];
            photos[i] = v;
            update("home", "photos", photos);
          }} />
        ))}
        <button onClick={saveSection.bind(null, "home")} disabled={saving}
          className="flex items-center gap-1 border border-[#FF5500] text-[#F4EFE6] px-4 py-1.5 text-xs hover:bg-[#FF5500] mt-2">
          <Save size={12} /> Save Home
        </button>
      </Section>

      {/* MUSIC */}
      <Section title="Music Page">
        <h4 className="font-mono-label text-xs text-[#FF5500] mb-2">Featured Release</h4>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Title" value={music.featured_release?.title} onChange={(v) => updateNested("music", "featured_release", "title", v)} />
          <Field label="Label" value={music.featured_release?.label} onChange={(v) => updateNested("music", "featured_release", "label", v)} />
          <Field label="Year" value={music.featured_release?.year} onChange={(v) => updateNested("music", "featured_release", "year", v)} />
          <Field label="Spotify Link" value={music.featured_release?.spotify_url} onChange={(v) => updateNested("music", "featured_release", "spotify_url", v)} />
          <Field label="Spotify Album Link" value={music.featured_release?.spotify_album_url} onChange={(v) => updateNested("music", "featured_release", "spotify_album_url", v)} />
        </div>
        <ImageField label="Release Artwork" value={music.featured_release?.artwork_url} onChange={(v) => updateNested("music", "featured_release", "artwork_url", v)} />
        <ListEditor label="Discography Grid" items={music.discography || []} setItems={(v) => update("music", "discography", v)}
          fields={[{key: "title", label: "Track Name"}, {key: "label", label: "Label"}, {key: "year", label: "Year"}, {key: "spotify_url", label: "Spotify Link"}, {key: "artwork_url", label: "Artwork URL (paste or upload via Media)"}]} />
        <button onClick={saveSection.bind(null, "music")} disabled={saving}
          className="flex items-center gap-1 border border-[#FF5500] text-[#F4EFE6] px-4 py-1.5 text-xs hover:bg-[#FF5500] mt-2">
          <Save size={12} /> Save Music
        </button>
      </Section>

      {/* SHOWS */}
      <Section title="Shows Page">
        <ListEditor label="Upcoming Shows" items={shows.upcoming || []} setItems={(v) => update("shows", "upcoming", v)}
          fields={[{key: "date", label: "Date"}, {key: "venue", label: "Venue"}, {key: "city", label: "City"}, {key: "details", label: "Details"}, {key: "link", label: "Ticket Link"}]} />
        <ListEditor label="Past Shows (Flyers)" items={shows.past || []} setItems={(v) => update("shows", "past", v)}
          fields={[{key: "title", label: "Show Name"}, {key: "date", label: "Year"}, {key: "flyer", label: "Flyer Image URL"}]} />
        <button onClick={saveSection.bind(null, "shows")} disabled={saving}
          className="flex items-center gap-1 border border-[#FF5500] text-[#F4EFE6] px-4 py-1.5 text-xs hover:bg-[#FF5500] mt-2">
          <Save size={12} /> Save Shows
        </button>
      </Section>

      {/* ABOUT */}
      <Section title="About Page">
        <ImageField label="Hero Background Image" value={about.hero_image} onChange={(v) => update("about", "hero_image", v)} />
        <Field label="Pull Quote (use <span> for orange words)" value={about.pull_quote} onChange={(v) => update("about", "pull_quote", v)} type="textarea" rows={2} />
        <ImageField label="Artist Photo" value={about.artist_photo} onChange={(v) => update("about", "artist_photo", v)} />
        <ListEditor label="Festivals & Venues" items={about.festivals || []} setItems={(v) => update("about", "festivals", v)}
          fields={[{key: "name", label: "Venue/Festival Name"}, {key: "detail", label: "Detail/Location"}]} />
        <Field label="Booking Email" value={about.booking_email} onChange={(v) => update("about", "booking_email", v)} />
        <Field label="Press Photos Google Drive URL" value={about.press_photos_drive_url} onChange={(v) => update("about", "press_photos_drive_url", v)} />
        <Field label="EPK URL" value={about.epk_url} onChange={(v) => update("about", "epk_url", v)} />
        <button onClick={saveSection.bind(null, "about")} disabled={saving}
          className="flex items-center gap-1 border border-[#FF5500] text-[#F4EFE6] px-4 py-1.5 text-xs hover:bg-[#FF5500] mt-2">
          <Save size={12} /> Save About
        </button>
      </Section>

      {/* CONTACT */}
      <Section title="Contact Page">
        <Field label="Booking Email" value={contact.booking_email} onChange={(v) => update("contact", "booking_email", v)} />
        <Field label="Location" value={contact.location} onChange={(v) => update("contact", "location", v)} />
        <button onClick={saveSection.bind(null, "contact")} disabled={saving}
          className="flex items-center gap-1 border border-[#FF5500] text-[#F4EFE6] px-4 py-1.5 text-xs hover:bg-[#FF5500] mt-2">
          <Save size={12} /> Save Contact
        </button>
      </Section>

      {/* SOCIAL LINKS */}
      <Section title="Social Links">
        <Field label="Instagram" value={social.instagram} onChange={(v) => update("social", "instagram", v)} />
        <Field label="SoundCloud" value={social.soundcloud} onChange={(v) => update("social", "soundcloud", v)} />
        <Field label="Spotify" value={social.spotify} onChange={(v) => update("social", "spotify", v)} />
        <Field label="YouTube" value={social.youtube} onChange={(v) => update("social", "youtube", v)} />
        <button onClick={saveSection.bind(null, "social")} disabled={saving}
          className="flex items-center gap-1 border border-[#FF5500] text-[#F4EFE6] px-4 py-1.5 text-xs hover:bg-[#FF5500] mt-2">
          <Save size={12} /> Save Social
        </button>
      </Section>
    </div>
  );
}
